import React, { useState, useEffect } from 'react';
import GoogleMapsLocationLink from '../../common/GoogleMapsLocationLink';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import ClickableImageGrid from '../../common/ClickableImageGrid';
import trendingService from '../../../services/trendingService';
import { getBusinessUrl } from '../../../utils/urlHelpers';
import { canManageSociety } from '../../../utils/societyPermissions';
import SocietyEditModal from '../SocietyEditModal';
import SocietyContactNumbers from '../SocietyContactNumbers';
import societyReviewService from '../../../services/societyReviewService';

const SocietyDetails = ({ society, onError, onNavigateToReviews, onSocietyUpdated }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [trendingItems, setTrendingItems] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(true);

  // Load society trending (manually curated businesses for this society)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!society?.id && !society?.slug) return;
      setTrendingLoading(true);
      try {
        const idOrSlug = society.slug ?? society.id;
        const results = await trendingService.getSocietyTrending(idOrSlug);
        if (!cancelled) {
          setTrendingItems((results || []).map((r) => r.business_data).filter(Boolean));
        }
      } catch (_) {
        if (!cancelled) setTrendingItems([]);
      } finally {
        if (!cancelled) setTrendingLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [society?.id, society?.slug]);

  // Load reviews for the details-tab preview (full CRUD on Reviews tab)
  useEffect(() => {
    if (!society?.id) {
      setReviews([]);
      return undefined;
    }

    let cancelled = false;

    const loadReviews = async () => {
      setLoading(true);
      try {
        const response = await societyReviewService.getSocietyReviews(society.id);
        const list = Array.isArray(response?.results)
          ? response.results
          : Array.isArray(response)
            ? response
            : [];
        if (!cancelled) setReviews(list);
      } catch (error) {
        console.error('Error loading reviews:', error);
        if (!cancelled) {
          setReviews([]);
          if (typeof onError === 'function') onError('Failed to load reviews');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [society?.id, onError]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  const showEdit = isAuthenticated && canManageSociety(user, society);

  return (
    <div className="space-y-6">
      <SocietyEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        society={society}
        onSaved={(updated) => {
          if (typeof onSocietyUpdated === 'function') onSocietyUpdated(updated);
        }}
      />
      {/* Society Description */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">About {society.name}</h2>
          {showEdit && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-md border border-emerald-600 text-emerald-700 text-sm font-medium hover:bg-emerald-50"
            >
              Edit details and photos
            </button>
          )}
        </div>
        <div className="prose max-w-none">
          <p className="text-gray-700 leading-relaxed text-sm sm:text-base whitespace-pre-line break-words">
            {society.description || 'No description available for this society.'}
          </p>
          <SocietyContactNumbers contacts={society.contact_numbers} />
        </div>
      </div>

      {/* Society Trending - manually curated businesses for this society */}
      {(trendingLoading || trendingItems.length > 0) && (
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Trending in this society</h3>
          {trendingLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex-shrink-0 w-[200px] rounded-xl bg-gray-100 animate-pulse aspect-[4/3]" />
              ))}
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2 md:overflow-visible md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4">
              {trendingItems.map((item) => {
                const imgUrl = item.display_image?.original_image || item.images?.[0]?.original_image;
                return (
                  <article
                    key={item.id}
                    className="flex-shrink-0 w-[200px] md:w-auto rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(getBusinessUrl(item))}
                  >
                    <div className="aspect-video bg-gray-100">
                      {imgUrl ? (
                        <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">{item.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{item.society?.city ?? ''}</p>
                      <p className="text-xs text-gray-600 mt-1">{(item.views || 0).toLocaleString()} views</p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(getBusinessUrl(item)); }}
                        className="mt-2 w-full py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                      >
                        View Details
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Society Images */}
      <ClickableImageGrid
        images={society.images}
        title="Photos"
        namePrefix={society.name}
        className="!p-4 sm:!p-6"
        gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2 sm:gap-4"
        imageClassName="w-full h-32 sm:h-48 object-cover rounded-lg"
      />

      {/* Reviews Section */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Reviews</h3>
          {isAuthenticated && (
            <button 
              onClick={() => {
                if (onNavigateToReviews) {
                  onNavigateToReviews();
                }
              }}
              className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Write Review
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {displayedReviews.map((review) => (
              <div key={review.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                <div className="mb-2">
                  <div className="font-medium text-gray-900">
                    {review.user_name || 'Anonymous'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {renderStars(review.rating)}
                    {review.created_at_display && (
                      <span className="text-sm text-gray-500">{review.created_at_display}</span>
                    )}
                  </div>
                </div>
                {review.review_text ? (
                  <p className="text-gray-700 text-sm sm:text-base whitespace-pre-line">
                    {review.review_text}
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm italic">No written comment.</p>
                )}
              </div>
            ))}

            {reviews.length > 3 && (
              <div className="text-center pt-4">
                <button
                  onClick={() => setShowAllReviews(!showAllReviews)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showAllReviews ? 'Show Less' : `Show All ${reviews.length} Reviews`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-gray-500">No reviews yet</p>
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => onNavigateToReviews?.()}
                className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Be the first to review
              </button>
            )}
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm sm:text-base text-gray-700 truncate">{society.city || 'Location not specified'}</span>
          </div>
          <GoogleMapsLocationLink
            latitude={society.latitude}
            longitude={society.longitude}
            city={society.city}
            name={society.name}
            label="View on Map"
          />
        </div>
      </div>
    </div>
  );
};

export default SocietyDetails;
